/**
 * Country-based compliance rules.
 * Supports GDPR (EU/EEA), UK GDPR, LGPD (Brazil), PIPEDA (Canada), and BASIC fallback.
 */

// EU member states + EEA
const GDPR_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', // EU 27
  'IS', 'LI', 'NO',                          // EEA non-EU
  'CH',                                       // Switzerland (nFADP, GDPR-aligned)
]);

const UK_COUNTRIES = new Set(['GB']);
const LGPD_COUNTRIES = new Set(['BR']);
const PIPEDA_COUNTRIES = new Set(['CA']);

/**
 * Resolve compliance mode from ISO 3166-1 alpha-2 country code.
 * @param {string} countryCode
 * @returns {'GDPR'|'UK_GDPR'|'LGPD'|'PIPEDA'|'BASIC'}
 */
export function getComplianceMode(countryCode) {
  if (!countryCode) return 'BASIC';
  const code = countryCode.toUpperCase();
  if (GDPR_COUNTRIES.has(code)) return 'GDPR';
  if (UK_COUNTRIES.has(code)) return 'UK_GDPR';
  if (LGPD_COUNTRIES.has(code)) return 'LGPD';
  if (PIPEDA_COUNTRIES.has(code)) return 'PIPEDA';
  return 'BASIC';
}

/**
 * Per-mode configuration.
 * requiresExplicitConsent  – true = must click accept (opt-in)
 * requiresRejectOption     – true = Reject All button must be visible
 * requiresGranularControl  – true = show per-category toggles
 * defaultDenied            – true = optional cookies start as OFF
 */
const EN_UI = {
  ariaLabel: 'Cookie consent',
  buttons: {
    acceptAll: 'Accept All',
    rejectAll: 'Reject All',
    customize: 'Customize',
    savePreferences: 'Save My Preferences',
  },
  tabs: {
    consent: 'Consent',
    details: 'Cookie Details',
    about: 'About',
  },
  about: {
    heading: 'About Cookies',
    intro:
      'Cookies are small text files stored on your device. They help websites function correctly, remember your preferences, and gather analytics.',
    legal:
      'The law states that we can store cookies on your device if they are strictly necessary for site operations. For all other types, we need your consent.',
    privacyPolicy: 'Privacy Policy',
    cookiePolicy: 'Cookie Policy',
  },
  table: {
    name: 'Name',
    domain: 'Domain',
    duration: 'Duration',
    description: 'Description',
  },
  categories: {
    alwaysActive: 'Always Active',
    optional: 'Optional',
    cannotDisable: 'Cannot be disabled',
    togglePrefix: 'Toggle ',
    noCookiesListed: 'No cookies listed for this category.',
  },
  branding: {
    poweredBy: 'Powered by',
  },
};

const PT_BR_UI = {
  ariaLabel: 'Consentimento de cookies',
  buttons: {
    acceptAll: 'Aceitar Todos',
    rejectAll: 'Rejeitar Todos',
    customize: 'Personalizar',
    savePreferences: 'Salvar Preferencias',
  },
  tabs: {
    consent: 'Consentimento',
    details: 'Detalhes dos Cookies',
    about: 'Sobre',
  },
  about: {
    heading: 'Sobre os Cookies',
    intro:
      'Cookies sao pequenos arquivos de texto armazenados no seu dispositivo. Eles ajudam os sites a funcionar corretamente, lembrar suas preferencias e coletar analises.',
    legal:
      'A lei determina que podemos armazenar cookies no seu dispositivo quando eles sao estritamente necessarios para o funcionamento do site. Para todos os outros tipos, precisamos do seu consentimento.',
    privacyPolicy: 'Politica de Privacidade',
    cookiePolicy: 'Politica de Cookies',
  },
  table: {
    name: 'Nome',
    domain: 'Dominio',
    duration: 'Duracao',
    description: 'Descricao',
  },
  categories: {
    alwaysActive: 'Sempre Ativo',
    optional: 'Opcional',
    cannotDisable: 'Nao pode ser desativado',
    togglePrefix: 'Alternar ',
    noCookiesListed: 'Nenhum cookie listado para esta categoria.',
  },
  branding: {
    poweredBy: 'Powered by',
  },
};

export const complianceConfig = {
  GDPR: {
    mode: 'GDPR',
    locale: 'en',
    ui: EN_UI,
    label: 'GDPR',
    requiresExplicitConsent: true,
    requiresRejectOption: true,
    requiresGranularControl: true,
    requiresPrivacyPolicy: true,
    defaultDenied: true,
    title: 'We Value Your Privacy',
    description:
      'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. You can choose which cookies to allow.',
    legalText:
      'By clicking "Accept All", you consent to our use of cookies. You may click "Customize" to provide controlled consent. Read our <a href="{privacyUrl}" target="_blank" rel="noopener">Privacy Policy</a>.',
  },
  UK_GDPR: {
    mode: 'UK_GDPR',
    locale: 'en',
    ui: EN_UI,
    label: 'UK GDPR',
    requiresExplicitConsent: true,
    requiresRejectOption: true,
    requiresGranularControl: true,
    requiresPrivacyPolicy: true,
    defaultDenied: true,
    title: 'We Value Your Privacy',
    description:
      'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. You can choose which cookies to allow.',
    legalText:
      'By clicking "Accept All", you consent to our use of cookies. You may click "Customize" to provide controlled consent. Read our <a href="{privacyUrl}" target="_blank" rel="noopener">Privacy Policy</a>.',
  },
  LGPD: {
    mode: 'LGPD',
    locale: 'pt-BR',
    ui: PT_BR_UI,
    label: 'LGPD',
    requiresExplicitConsent: true,
    requiresRejectOption: true,
    requiresGranularControl: true,
    requiresPrivacyPolicy: true,
    defaultDenied: true,
    title: 'Valorizamos sua privacidade',
    description:
      'Utilizamos cookies para melhorar sua experiência de navegação, veicular conteúdo personalizado e analisar nosso tráfego. Você pode escolher quais cookies permitir.',
    legalText:
      'Ao clicar em "Aceitar Todos", você consente com o uso de cookies. Acesse "Personalizar" para escolher quais aceitar. Leia nossa <a href="{privacyUrl}" target="_blank" rel="noopener">Política de Privacidade</a>.',
  },
  PIPEDA: {
    mode: 'PIPEDA',
    locale: 'en',
    ui: EN_UI,
    label: 'PIPEDA',
    requiresExplicitConsent: false,
    requiresRejectOption: true,
    requiresGranularControl: true,
    requiresPrivacyPolicy: true,
    defaultDenied: false,
    title: 'We Use Cookies',
    description:
      'We use cookies to improve your experience on our website. You may withdraw consent at any time.',
    legalText:
      'By continuing to use our site, you consent to our use of cookies. Read our <a href="{privacyUrl}" target="_blank" rel="noopener">Privacy Policy</a>.',
  },
  BASIC: {
    mode: 'BASIC',
    locale: 'en',
    ui: EN_UI,
    label: null,
    requiresExplicitConsent: false,
    requiresRejectOption: true,
    requiresGranularControl: true,
    requiresPrivacyPolicy: false,
    defaultDenied: false,
    title: 'We Use Cookies',
    description:
      'We use cookies to improve your experience on our website. You can accept all cookies, reject optional ones, or customize your preferences.',
    legalText:
      'You can click "Accept All" to enable all cookies, "Reject All" to keep only the necessary ones, or "Customize" to choose your preferences. Read our <a href="{privacyUrl}" target="_blank" rel="noopener">Privacy Policy</a>.',
  },
};
